/**
 * DocumentPreview — renderiza un archivo adjunto a un claim.
 *
 * Decide qué viewer usar según la extensión del URL:
 *  - .pdf → PdfViewer (react-pdf con worker configurado, retry, fallback iframe).
 *  - .jpg/.jpeg/.png/.webp/.gif → <img> con click-to-zoom (Dialog).
 *  - otros → link de descarga.
 */

import { useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import { PdfViewer } from '@app/components/PdfViewer';
import { Download, FileText, Image as ImageIcon, ExternalLink, Maximize } from 'lucide-react';
import { cn } from '@shared/utils/cn';

type Kind = 'pdf' | 'image' | 'other';

function detectKind(url: string): Kind {
  const lower = url.split('?')[0].toLowerCase();
  if (lower.endsWith('.pdf')) return 'pdf';
  if (/\.(jpe?g|png|webp|gif|bmp|avif)$/.test(lower)) return 'image';
  return 'other';
}

function filenameFromUrl(url: string): string {
  try {
    const pathname = new URL(url).pathname;
    return decodeURIComponent(pathname.split('/').pop() ?? 'archivo');
  } catch {
    return url.split('/').pop() ?? 'archivo';
  }
}

interface DocumentPreviewProps {
  url: string;
  label?: string;
  /** Si es true, el preview ocupa ancho completo. Por default, queda en una card. */
  fullWidth?: boolean;
  className?: string;
}

export function DocumentPreview({ url, label, fullWidth = false, className }: DocumentPreviewProps) {
  const kind = detectKind(url);
  const filename = filenameFromUrl(url);
  const [zoomOpen, setZoomOpen] = useState(false);

  return (
    <div
      className={cn(
        'rounded-2xl border border-border bg-card p-4 shadow-xs',
        fullWidth ? 'w-full' : '',
        className,
      )}
    >
      {/* Header: label + filename + acciones */}
      <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
        <div className="flex min-w-0 items-center gap-2">
          {kind === 'pdf' && (
            <FileText className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          {kind === 'image' && (
            <ImageIcon className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          {kind === 'other' && (
            <ExternalLink className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden="true" />
          )}
          <div className="min-w-0">
            {label && (
              <p className="text-xs font-medium uppercase tracking-wide text-muted-foreground">
                {label}
              </p>
            )}
            <p className="truncate text-sm font-medium text-foreground" title={filename}>
              {filename}
            </p>
          </div>
        </div>
        <div className="flex shrink-0 gap-1">
          {kind === 'image' && (
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8 rounded-full"
              onClick={() => setZoomOpen(true)}
              aria-label="Ampliar imagen"
            >
              <Maximize className="h-4 w-4" aria-hidden="true" />
            </Button>
          )}
          <Button asChild variant="ghost" size="icon" className="h-8 w-8 rounded-full">
            <a href={url} target="_blank" rel="noopener noreferrer" download aria-label="Descargar archivo">
              <Download className="h-4 w-4" aria-hidden="true" />
            </a>
          </Button>
        </div>
      </div>

      {/* Preview según tipo */}
      {kind === 'pdf' && (
        <div className="overflow-hidden rounded-xl border border-border bg-muted/30 p-2">
          <PdfViewer url={url} width={520} />
        </div>
      )}

      {kind === 'image' && (
        <button
          type="button"
          onClick={() => setZoomOpen(true)}
          className="block w-full overflow-hidden rounded-xl border border-border bg-muted/30 transition-shadow hover:shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          aria-label="Ampliar imagen"
        >
          <img
            src={url}
            alt={label ?? filename}
            loading="lazy"
            className="block max-h-96 w-full object-contain"
          />
        </button>
      )}

      {kind === 'other' && (
        <a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          className="inline-flex items-center gap-1 text-sm font-medium text-sidebar-primary hover:underline"
        >
          Abrir en nueva pestaña
          <ExternalLink className="h-3.5 w-3.5" aria-hidden="true" />
        </a>
      )}

      {/* Dialog zoom para imagen */}
      {kind === 'image' && (
        <Dialog open={zoomOpen} onOpenChange={setZoomOpen}>
          <DialogContent className="max-w-5xl p-2 sm:p-4">
            <DialogTitle className="sr-only">{label ?? filename}</DialogTitle>
            <DialogDescription className="sr-only">Vista ampliada del documento</DialogDescription>
            <img
              src={url}
              alt={label ?? filename}
              className="block max-h-[80vh] w-full object-contain"
            />
          </DialogContent>
        </Dialog>
      )}
    </div>
  );
}
